import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, LOGO_IMG } from "@/lib/site-data";

const Wordmark = ({ onClick }) => (
  <a
    href="#top"
    onClick={onClick}
    data-testid="nav-logo"
    className="inline-flex items-center"
  >
    <img src={LOGO_IMG} alt="Avatar India" className="h-8 w-auto md:h-10" />
  </a>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href === "#top" ? "body" : href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
          scrolled ? "glass border-b border-white/10" : "border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-12">
          <Wordmark onClick={(e) => go(e, "#top")} />

          <div className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="group relative text-sm font-medium text-avatar-ink/60 transition-colors duration-300 hover:text-avatar-ink"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-avatar-red transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="#dealers"
              onClick={(e) => go(e, "#dealers")}
              data-testid="nav-dealer-cta"
              className="hidden items-center gap-1.5 rounded-full bg-avatar-red px-6 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#c62828] md:inline-flex"
            >
              Become a Dealer <ArrowUpRight size={16} />
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-avatar-ink/15 text-avatar-ink lg:hidden"
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex flex-col justify-center gap-2 bg-avatar-paper px-8 lg:hidden"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
                className="font-heading text-4xl font-bold tracking-tight text-avatar-ink/90"
              >
                {l.label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
